import { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  Typography,
  Button,
  Avatar,
  Tooltip,
  Popover,
  InputNumber,
  message,
  Divider,
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  DownloadOutlined,
  CheckCircleTwoTone,
  CheckCircleOutlined,
  ClockCircleTwoTone,
  EditOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useBatches, batchLabel } from '../hooks/useBatches';
import { useEvalRoleLabels } from '../contexts/EvalRoleLabelsContext';
import { useTalentPoolLabels } from '../contexts/TalentPoolLabelsContext';
import { taskStatusLabel, taskStatusColor } from '../utils/taskStatusDisplay';
import { LEVEL_COLOR } from '../utils/scoreColor';
import {
  adminPageWrap,
  adminFilterCardStyle,
  adminContentCardStyle,
} from '../styles/adminPageShell';

const { Text } = Typography;

const POOL_COLORS: Record<string, string> = {
  EXECUTIVE: 'red',
  MIDDLE_MGMT: 'volcano',
  BACKEND: 'blue',
  FINANCE_DATA: 'cyan',
  MARKETING_ACQUISITION: 'purple',
  SALES: 'gold',
  MEDICAL_OPS: 'green',
};

const DONE_STATUSES = new Set(['SUBMITTED', 'LOCKED']);
const isDone = (status: string) => DONE_STATUSES.has(status);

/** 人才池筛选「全部」哨兵值（不与真实池 code 冲突） */
const ALL_POOLS = '__ALL__';

interface ScoreRow {
  dimCode: string;
  score: number;
  comment?: string | null;
  // V1.3 校准字段（adjustedFromScore !== null 即被 admin 校准过）
  adjustedFromScore?: number | null;
  adjustedById?: number | null;
  adjustedAt?: string | null;
  adjustedBy?: { id: number; name: string } | null;
}

interface TemplateDim {
  code: string;
  name?: string;
  desc?: string;
  anchors?: Array<{ range: string; level: string; desc: string }>;
}

interface RawTask {
  id: number;
  evaluator: { id: number; name: string };
  evaluatee: { id: number; name: string; deptPath?: string | null };
  template: {
    pool: string;
    dimensions?: TemplateDim[] | null;
    fullScore?: number | null;
    dimensionCount?: number | null;
    relationWeights?: Record<string, number> | null;
  };
  roleInEval: string;
  status: string;
  submitDuration?: number | null;
  submitAt?: string | null;
  scores?: ScoreRow[];
}

// 用于聚合计算（每个被评价人一条）：加权汇总分、标准分、池内排名、素质档位
interface AggResult {
  weightedTotal: number; // Σ(代表总分 × 归一权重)
  standardScore: number; // weighted/(7n) × 100，保留 2 位
  fullScore: number; // 7n（用于在 CSV 里写满分参考）
  validRoles: number; // 有效角色类数（>=1 才算有结果）
}

/** 每行满分按该行自己的模板算（「全部」模式下各池模板不同，不能统一用当前池维度数 × 7） */
function taskFullScore(t: RawTask): number {
  const n = t.template.dimensionCount ?? (t.template.dimensions || []).length;
  return t.template.fullScore ?? n * 7;
}

function shortDept(path: string | null | undefined) {
  if (!path) return '-';
  const parts = path
    .split(/[\/\\>›→]/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 2) return parts.join(' / ');
  return parts.slice(-2).join(' / ');
}

/**
 * 评价结果一览（按"被评价人 × 评价人 × 维度"扁平展开）
 *
 * 需求：HR 想一眼看到「这个人被谁评了，那个评价人是什么角色，每一道维度多少分」。
 *
 * 设计：
 *   1. 必须选批次（数据量按批次切，避免一次拉穿）
 *   2. 必须选人才池（不同池模板维度不同，硬合一张表会列爆炸 / 维度名错位）
 *   3. 同一被评价人占多行（多评价人各占一行），第一列 rowSpan 合并
 *   4. 已提交：每个维度显示该评价人给的分；未提交：维度列显示 "—"，状态显示「待提交」
 *   5. 顶部"导出 CSV"：把当前筛选下的扁平表导出（HR 拿去写 PPT）
 *
 * 与现有「评价进度」页（EvalTasksPage）的区分：
 *   - 那个页面：按被评人聚合的进度看板，详情藏在 Modal 里
 *   - 本页面：直接铺平的二维矩阵，HR 横向看维度分布
 */
type KwField = 'all' | 'evaluatee' | 'evaluator' | 'dept';

const KW_FIELD_OPTIONS: { value: KwField; label: string; placeholder: string }[] = [
  { value: 'all', label: '全部', placeholder: '搜索被评价人 / 评价人 / 部门' },
  { value: 'evaluatee', label: '被评价人', placeholder: '输入被评价人姓名' },
  { value: 'evaluator', label: '评价人', placeholder: '输入评价人姓名' },
  { value: 'dept', label: '部门', placeholder: '输入部门关键字' },
];

/** 评价结果一览表头：维度名 + ● 浅色 Popover（替代整列悬停深色 Tooltip），含说明与各档锚点 */
function ResultsMatrixDimHeader({ dim }: { dim: TemplateDim }) {
  const label = dim.name || dim.code;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        width: '100%',
      }}
    >
      <span style={{ fontWeight: 500 }}>{label}</span>
      <Popover
        trigger={['click']}
        placement="bottom"
        overlayStyle={{ maxWidth: 440 }}
        styles={{
          body: {
            background: '#fff',
            boxShadow: '0 6px 24px rgba(15, 23, 42, 0.12)',
          },
        }}
        content={<ResultsMatrixDimRubricBody dim={dim} />}
      >
        <button
          type="button"
          aria-label={`${label}：维度说明与评分标准`}
          title="说明与评分标准"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: '#722ed1',
            fontSize: 11,
            lineHeight: 1,
            borderRadius: '50%',
          }}
        >
          ●
        </button>
      </Popover>
    </div>
  );
}

function ResultsMatrixDimRubricBody({ dim }: { dim: TemplateDim }) {
  const anchors = dim.anchors?.length ? dim.anchors : null;

  return (
    <div style={{ maxWidth: 400, maxHeight: 'min(55vh, 420px)', overflow: 'auto' }}>
      {!anchors && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          未带出行为锚点；请检查素质模板数据或 HR 下发文档。
        </Text>
      )}
      <Text strong style={{ display: 'block', marginBottom: 6 }}>
        维度说明
      </Text>
      <Typography.Paragraph style={{ marginBottom: 10, whiteSpace: 'pre-wrap', fontSize: 13 }}>
        {dim.desc || `（暂无 ${dim.code} 的维度说明文案）`}
      </Typography.Paragraph>
      <Divider plain style={{ margin: '10px 0' }}>
        各区间的行为锚点
      </Divider>
      {anchors ? (
        anchors.map((a) => (
          <div key={a.range} style={{ marginBottom: 8 }}>
            <Tag color={(LEVEL_COLOR as Record<string, string>)[a.level] ?? 'processing'}>
              {a.range} · {a.level}
            </Tag>
            <div style={{ color: '#555', fontSize: 12, marginTop: 4, lineHeight: 1.55 }}>{a.desc}</div>
          </div>
        ))
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          （无）
        </Text>
      )}
    </div>
  );
}

/**
 * 同一被评人下，多个评价人按下面的关系优先级排（数字越小越靠前）。
 * SELF 一律压到末尾——自评权重 0，不参与加权，但 task 仍存在；放最下方避免干扰。
 *
 * 业务定义来源：用户 2026-04-27 指令。
 *   - 综合职能 / 销售 / 财务 / 医护池：直属 → 斜线 → 本部门 → 跨部门
 *   - 中层管理池：直属 → 斜线 → 下属 → 同级
 *   - 高层管理池：直属 → 其他领导 → 其他领导 → 下属/其他领导
 *     （EvalRole 没有 OTHER_LEADER_1/2 之分，DB 里都是 OTHER_LEADER；同 role 多人按 taskId 升序）
 */
const ROLE_PRIORITY_DEFAULT: Record<string, number> = {
  DIRECT: 1,
  DIAGONAL: 2,
  SAME_DEPT: 3,
  CROSS_DEPT: 4,
  SUBORDINATE: 5,
  PEER_LEADER: 6,
  OTHER_LEADER: 7,
  SELF: 99,
};
const ROLE_PRIORITY_BY_POOL: Record<string, Record<string, number>> = {
  MIDDLE_MGMT: {
    DIRECT: 1,
    DIAGONAL: 2,
    SUBORDINATE: 3,
    PEER_LEADER: 4,
    SAME_DEPT: 5,
    CROSS_DEPT: 6,
    OTHER_LEADER: 7,
    SELF: 99,
  },
  EXECUTIVE: {
    DIRECT: 1,
    OTHER_LEADER: 2,
    SUBORDINATE: 3,
    DIAGONAL: 4,
    SAME_DEPT: 5,
    CROSS_DEPT: 6,
    PEER_LEADER: 7,
    SELF: 99,
  },
};

function rolePriority(pool: string, role: string): number {
  const map = ROLE_PRIORITY_BY_POOL[pool] || ROLE_PRIORITY_DEFAULT;
  return map[role] ?? 90;
}

export function EvalResultsMatrixPage() {
  const { getEvalRoleLabel } = useEvalRoleLabels();
  const { getTalentPoolLabel } = useTalentPoolLabels();
  const { batches, batchId, setBatchId, isLoading: loadingBatches } = useBatches();
  // 默认「全部」：进页面即显示所有人，不再强制选池
  const [pool, setPool] = useState<string>(ALL_POOLS);
  const [keyword, setKeyword] = useState('');
  const [kwField, setKwField] = useState<KwField>('all');
  const queryClient = useQueryClient();

  const { data = [], isLoading } = useQuery<RawTask[]>({
    queryKey: ['eval-results-matrix', { batchId }],
    queryFn: () => api.get('/admin/eval-tasks', { params: { batchId } }),
    enabled: !!batchId,
  });

  const { data: calibrationResp } = useQuery<{ byEvaluateeId: Record<string, boolean> }>({
    queryKey: ['quality-calibration-reviews', batchId],
    queryFn: () =>
      api.get('/admin/quality-calibration-reviews', { params: { batchId } }),
    enabled: !!batchId,
  });

  const calibrationByEvaluatee = useMemo(() => {
    const src = calibrationResp?.byEvaluateeId || {};
    const out = new Map<number, boolean>();
    for (const [k, v] of Object.entries(src)) {
      const id = Number(k);
      if (Number.isFinite(id)) out.set(id, !!v);
    }
    return out;
  }, [calibrationResp]);

  const rowCalibrationMutation = useMutation({
    mutationFn: (vars: { evaluateeId: number; completed: boolean }) =>
      api.post('/admin/quality-calibration-reviews', {
        batchId,
        evaluateeId: vars.evaluateeId,
        completed: vars.completed,
      }),
    onMutate: async (vars) => {
      if (batchId == null) return {};
      const qk = ['quality-calibration-reviews', batchId] as const;
      await queryClient.cancelQueries({ queryKey: qk });
      const prev = queryClient.getQueryData<{ byEvaluateeId: Record<string, boolean> }>(qk);
      queryClient.setQueryData<{ byEvaluateeId: Record<string, boolean> }>(qk, (old) => ({
        byEvaluateeId: {
          ...(old?.byEvaluateeId || {}),
          [String(vars.evaluateeId)]: vars.completed,
        },
      }));
      return { prev, qk };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.qk != null && 'prev' in ctx) {
        queryClient.setQueryData(ctx.qk, ctx.prev);
      }
      const m = e?.response?.data?.message;
      const text =
        typeof m === 'string'
          ? m
          : Array.isArray(m)
            ? m.map(String).join('；')
            : m != null
              ? JSON.stringify(m)
              : e?.message || '更新失败';
      message.error(text);
    },
    onSuccess: () => {
      message.success('已更新校准状态');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['quality-calibration-reviews'] });
    },
  });

  // 校准单维度分（V1.3）。成功后 invalidate 列表，加权总分/标准分/等级会自动重算
  const adjustMutation = useMutation({
    mutationFn: (vars: { taskId: number; dimCode: string; score: number }) =>
      api.patch(`/admin/eval-tasks/${vars.taskId}/scores/${vars.dimCode}`, {
        score: vars.score,
      }),
    onSuccess: () => {
      message.success('已校准');
      queryClient.invalidateQueries({ queryKey: ['eval-results-matrix'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || '校准失败');
    },
  });

  // 池清单：从当前批次的实际数据里抽取，没有的池不显示选项；首项固定「全部」
  const poolOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of data) if (t.template?.pool) set.add(t.template.pool);
    return [
      { value: ALL_POOLS, label: '全部' },
      ...Array.from(set).map((p) => ({ value: p, label: getTalentPoolLabel(p) })),
    ];
  }, [data, getTalentPoolLabel]);

  const effectivePool = pool;
  const isAllPools = effectivePool === ALL_POOLS;

  // 当前池下的扁平 task 列表（「全部」= 不按池过滤）
  const tasksOfPool = useMemo(() => {
    if (isAllPools) return data;
    return data.filter((t) => t.template?.pool === effectivePool);
  }, [data, effectivePool, isAllPools]);

  // 该池的模板维度（动态列的来源）。同池可能有多个模板（罕见），这里取出现 task 数最多那份模板的维度。
  // 「全部」模式不铺维度明细列（各池模板不同，硬合会列爆炸/维度错位），只保留公共列。
  const dims: TemplateDim[] = useMemo(() => {
    if (isAllPools) return [];
    if (tasksOfPool.length === 0) return [];
    const counter = new Map<string, { count: number; dims: TemplateDim[] }>();
    for (const t of tasksOfPool) {
      const ds = t.template?.dimensions || [];
      const sig = ds.map((d) => d.code).join('|');
      const cur = counter.get(sig);
      if (cur) cur.count += 1;
      else counter.set(sig, { count: 1, dims: ds });
    }
    let best: { count: number; dims: TemplateDim[] } | null = null;
    for (const v of counter.values()) {
      if (!best || v.count > best.count) best = v;
    }
    return best?.dims || [];
  }, [tasksOfPool, isAllPools]);

  // 应用关键字 + 排序：先按被评价人姓名（拼音），再按 task id 升序（角色顺序稳定）
  const flatRows = useMemo(() => {
    const kw = keyword.trim();
    const filtered = tasksOfPool.filter((t) => {
      if (!kw) return true;
      const dept = t.evaluatee.deptPath || '';
      switch (kwField) {
        case 'evaluatee':
          return t.evaluatee.name.includes(kw);
        case 'evaluator':
          return t.evaluator.name.includes(kw);
        case 'dept':
          return dept.includes(kw);
        default:
          return (
            t.evaluatee.name.includes(kw) ||
            t.evaluator.name.includes(kw) ||
            dept.includes(kw)
          );
      }
    });
    return filtered.sort((a, b) => {
      const cmp = a.evaluatee.name.localeCompare(b.evaluatee.name, 'zh-CN');
      if (cmp !== 0) return cmp;
      // 同被评价人：按池关系优先级排（直属 > 斜线 > 同事 ...，自评压底）
      const pa = rolePriority(a.template.pool, a.roleInEval);
      const pb = rolePriority(b.template.pool, b.roleInEval);
      if (pa !== pb) return pa - pb;
      // 相同 role（如高层池 OTHER_LEADER × 2）：按 taskId 升序定先后
      return a.id - b.id;
    });
  }, [tasksOfPool, keyword, kwField]);

  // 顶部统计
  const stats = useMemo(() => {
    const total = flatRows.length;
    const submitted = flatRows.filter((t) => isDone(t.status)).length;
    const evaluatees = new Set(flatRows.map((t) => t.evaluatee.id)).size;
    return { total, submitted, pending: total - submitted, evaluatees };
  }, [flatRows]);

  /**
   * 按规则计算每个被评价人的加权总分（全批次范围，不分池）。
   *
   * 规则来源：核算引擎/README.md §核算规则 + score.engine.ts
   *   1. 满分 = 7n（template.fullScore；缺失时用 dimensions.length × 7 兜底）
   *   2. 完整提交：status ∈ {SUBMITTED, LOCKED} 且每个 dim 都填了分（缺一即整份废弃）
   *   3. 同一 roleInEval 多人 → 各人「全维度总分」求平均得"代表总分"
   *   4. 缺类归一：W' = W / Σ(W_有效)
   *   5. 加权汇总分 = Σ(代表总分 × W')
   *   6. 标准分 = 加权汇总分 ÷ 满分 × 100（保留 2 位）
   *
   * 范围：基于 data（当前批次所有任务），不基于 tasksOfPool。
   * 因为等级要按"全员"排，标准分本身又是池内可比的（都是 0-100 满分），所以全员可直接比较。
   * 切人才池筛选不影响每人的标准分和等级。
   */
  const aggByEvaluatee = useMemo(() => {
    const byEvaluatee = new Map<number, RawTask[]>();
    for (const t of data) {
      const arr = byEvaluatee.get(t.evaluatee.id) ?? [];
      arr.push(t);
      byEvaluatee.set(t.evaluatee.id, arr);
    }
    const out = new Map<number, AggResult>();
    for (const [evaId, tasks] of byEvaluatee.entries()) {
      const tpl = tasks[0]?.template;
      const dimCodes = (tpl?.dimensions || []).map((d) => d.code);
      const n = tpl?.dimensionCount ?? dimCodes.length;
      const fullScore = tpl?.fullScore ?? 7 * n;
      const weights: Record<string, number> = (tpl?.relationWeights as any) || {};
      if (n === 0 || !weights || Object.keys(weights).length === 0) continue;

      // 同 roleInEval 内所有「完整提交」的全维度总分
      const totalsByRole = new Map<string, number[]>();
      for (const t of tasks) {
        if (!isDone(t.status)) continue;
        const provided = new Set((t.scores || []).map((s) => s.dimCode));
        const complete = dimCodes.every((c) => provided.has(c));
        if (!complete) continue;
        const total = (t.scores || []).reduce((s, x) => s + (x.score || 0), 0);
        const arr = totalsByRole.get(t.roleInEval) ?? [];
        arr.push(total);
        totalsByRole.set(t.roleInEval, arr);
      }
      if (totalsByRole.size === 0) continue;

      // 代表总分 + 缺类归一
      const repByRole = new Map<string, number>();
      for (const [role, ts] of totalsByRole.entries()) {
        repByRole.set(role, ts.reduce((s, x) => s + x, 0) / ts.length);
      }
      const activeWeights: [string, number][] = [];
      for (const role of repByRole.keys()) {
        const w = weights[role] ?? 0;
        if (w > 0) activeWeights.push([role, w]);
      }
      if (activeWeights.length === 0) continue;
      const sumW = activeWeights.reduce((s, [, w]) => s + w, 0);

      let weighted = 0;
      for (const [role, w] of activeWeights) {
        const wPrime = w / sumW;
        weighted += (repByRole.get(role) ?? 0) * wPrime;
      }
      const standard = (weighted / fullScore) * 100;
      out.set(evaId, {
        weightedTotal: Math.round(weighted * 100) / 100,
        standardScore: Math.round(standard * 100) / 100,
        fullScore,
        validRoles: activeWeights.length,
      });
    }
    return out;
  }, [data]);

  /**
   * 全员等级（高/中/低）—— 基于「全批次所有有标准分的被评人」排序，不分池。
   * 仅有标准分的被评人参与排序，没分的显示 —。
   *   - 总数 < 5：高 1、低 1
   *   - 5–14：高 ≤ 3、低 ≤ 2（按比例向上取整）
   *   - ≥ 15：前 30% 高、中 50% 中、后 20% 低
   */
  const gradeByEvaluatee = useMemo(() => {
    const ranked = Array.from(aggByEvaluatee.entries())
      .map(([evaId, agg]) => ({ evaId, score: agg.standardScore }))
      .sort((a, b) => b.score - a.score);
    const size = ranked.length;
    const out = new Map<number, { grade: '高' | '中' | '低'; rank: number; total: number }>();
    if (size === 0) return out;
    let highN: number;
    let lowN: number;
    if (size < 5) {
      highN = 1;
      lowN = 1;
    } else if (size < 15) {
      highN = Math.min(3, Math.max(1, Math.ceil(size * 0.3)));
      lowN = Math.min(2, Math.max(1, Math.ceil(size * 0.2)));
    } else {
      highN = Math.ceil(size * 0.3);
      lowN = Math.ceil(size * 0.2);
    }
    ranked.forEach((r, i) => {
      let grade: '高' | '中' | '低';
      if (i < highN) grade = '高';
      else if (i >= size - lowN) grade = '低';
      else grade = '中';
      out.set(r.evaId, { grade, rank: i + 1, total: size });
    });
    return out;
  }, [aggByEvaluatee]);

  // rowSpan：同被评价人合并第一列
  const evaluateeRowSpan = (idx: number): number => {
    const cur = flatRows[idx];
    const prev = idx > 0 ? flatRows[idx - 1] : null;
    if (prev && prev.evaluatee.id === cur.evaluatee.id) return 0; // 跟上一行同人，本行不出现
    let span = 1;
    while (
      flatRows[idx + span] &&
      flatRows[idx + span].evaluatee.id === cur.evaluatee.id
    ) {
      span += 1;
    }
    return span;
  };

  /** 被评价人分组元数据：整组同色斑马纹 + 组末分隔线（不逐 task 行交替） */
  const evaluateeGroupMeta = useMemo(() => {
    const meta = new Map<number, { groupIndex: number; isGroupEnd: boolean }>();
    let groupIndex = -1;
    let lastEvaluateeId: number | null = null;
    for (let i = 0; i < flatRows.length; i++) {
      const cur = flatRows[i];
      if (cur.evaluatee.id !== lastEvaluateeId) {
        groupIndex += 1;
        lastEvaluateeId = cur.evaluatee.id;
      }
      const isGroupEnd =
        i === flatRows.length - 1 || flatRows[i + 1].evaluatee.id !== cur.evaluatee.id;
      meta.set(cur.id, { groupIndex, isGroupEnd });
    }
    return meta;
  }, [flatRows]);

  const mergedEvaluateeCell = (idx: number) => {
    const span = evaluateeRowSpan(idx);
    return {
      rowSpan: span,
      ...(span > 0 ? { className: 'tg-eval-group-span-cell' } : {}),
    };
  };

  const evaluateeGroupRowClassName = (record: RawTask) => {
    // 按 task id 取元数据，避免 Ant Table 翻页后 index 从 0 重算导致条纹/组末线错位。
    const meta = evaluateeGroupMeta.get(record.id);
    if (!meta) return '';
    return [
      meta.groupIndex % 2 === 0 ? 'tg-eval-group-a' : 'tg-eval-group-b',
      meta.isGroupEnd ? 'tg-eval-group-end' : '',
    ]
      .filter(Boolean)
      .join(' ');
  };

  // 维度列动态生成：每个维度一列，已提交的行显示分数（可点击校准），未提交显示 —
  const dimColumns = dims.map((d) => ({
    title: <ResultsMatrixDimHeader dim={d} />,
    key: `dim_${d.code}`,
    width: 98,
    align: 'center' as const,
    render: (_v: unknown, t: RawTask) => {
      if (!isDone(t.status)) return <Text type="secondary">—</Text>;
      const sc = t.scores?.find((s) => s.dimCode === d.code);
      if (!sc) return <Text type="secondary">—</Text>;
      return (
        <ScoreCell
          score={sc}
          dimName={d.name || d.code}
          onSubmit={(newScore) =>
            adjustMutation.mutateAsync({ taskId: t.id, dimCode: d.code, score: newScore })
          }
          submitting={adjustMutation.isPending}
        />
      );
    },
  }));

  // 「全部」模式下多一列人才池标识，便于区分各行所属的池
  const poolColumn = isAllPools
    ? [
        {
          title: '人才池',
          key: 'pool',
          fixed: 'left' as const,
          width: 110,
          onCell: (_t: RawTask, idx?: number) => mergedEvaluateeCell(idx ?? 0),
          render: (_v: unknown, t: RawTask) => (
            <Tag color={POOL_COLORS[t.template.pool] || 'default'} style={{ margin: 0 }}>
              {getTalentPoolLabel(t.template.pool)}
            </Tag>
          ),
        },
      ]
    : [];

  const columns = [
    {
      title: '被评价人',
      key: 'evaluatee',
      fixed: 'left' as const,
      width: 150,
      onCell: (_t: RawTask, idx?: number) => mergedEvaluateeCell(idx ?? 0),
      render: (_v: unknown, t: RawTask) => (
        <Space size={6}>
          <Avatar size={26} icon={<UserOutlined />} style={{ background: '#1677ff' }}>
            {t.evaluatee.name?.[0]}
          </Avatar>
          <span style={{ fontWeight: 600 }}>{t.evaluatee.name}</span>
        </Space>
      ),
    },
    {
      title: '部门',
      key: 'dept',
      fixed: 'left' as const,
      width: 160,
      onCell: (_t: RawTask, idx?: number) => mergedEvaluateeCell(idx ?? 0),
      render: (_v: unknown, t: RawTask) => (
        <Text style={{ fontSize: 12, color: '#666' }}>{shortDept(t.evaluatee.deptPath)}</Text>
      ),
    },
    ...poolColumn,
    {
      title: (
        <Tooltip title="与「单维度改分校准」无关：表示 HR 对本人在本批次素质项评分整体核对完毕。可反复切换。">
          <span>校准</span>
        </Tooltip>
      ),
      key: 'rowCalibration',
      fixed: 'left' as const,
      width: 104,
      align: 'center' as const,
      onCell: (_t: RawTask, idx?: number) => mergedEvaluateeCell(idx ?? 0),
      render: (_v: unknown, t: RawTask) => {
        const done = calibrationByEvaluatee.get(t.evaluatee.id) === true;
        const rowLoading =
          rowCalibrationMutation.isPending &&
          rowCalibrationMutation.variables?.evaluateeId === t.evaluatee.id;
        return (
          <Button
            type={done ? 'primary' : 'default'}
            size="small"
            loading={rowLoading}
            disabled={!batchId}
            icon={
              done ? (
                <CheckCircleOutlined />
              ) : (
                <WarningOutlined style={{ color: '#fa8c16' }} />
              )
            }
            onClick={() =>
              rowCalibrationMutation.mutate({
                evaluateeId: t.evaluatee.id,
                completed: !done,
              })
            }
            style={
              done
                ? {
                    background: '#389e0d',
                    borderColor: '#389e0d',
                    fontWeight: 600,
                  }
                : {
                    borderColor: '#fa8c16',
                    color: '#d46b08',
                    fontWeight: 600,
                  }
            }
          >
            {done ? '已校准' : '待校准'}
          </Button>
        );
      },
    },
    {
      title: '评价人',
      key: 'evaluator',
      fixed: 'left' as const,
      width: 120,
      render: (_v: unknown, t: RawTask) => <span>{t.evaluator.name}</span>,
    },
    {
      title: (
        <Tooltip title="该评价关系在所属人才池里的原始权重；自评 0% 不计入加权">
          <span>关系（权重）</span>
        </Tooltip>
      ),
      key: 'role',
      width: 200,
      render: (_v: unknown, t: RawTask) => {
        // ⚠️ DB 里 relationWeights 存的是百分数（50/20/10/0），不是 0-1 小数。直接渲染勿乘 100。
        // （加权计算那边走 w/sumW 归一化，单位不影响结果。）
        const w = (t.template.relationWeights as Record<string, number> | null | undefined)?.[
          t.roleInEval
        ];
        const wPct = typeof w === 'number' ? w : null;
        return (
          <Space size={4} style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap' }}>
            <Tag color="blue" style={{ margin: 0 }}>
              {getEvalRoleLabel(t.template.pool, t.roleInEval)}
            </Tag>
            {wPct != null && (
              <Tag
                color={wPct === 0 ? 'default' : 'cyan'}
                style={{ margin: 0, fontSize: 11, fontWeight: 500 }}
              >
                {wPct}%
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '状态',
      key: 'status',
      width: 90,
      render: (_v: unknown, t: RawTask) => (
        <Tag color={taskStatusColor(t.status)}>{taskStatusLabel(t.status)}</Tag>
      ),
    },
    ...dimColumns,
    {
      title: '总分',
      key: 'sum',
      width: 90,
      align: 'center' as const,
      render: (_v: unknown, t: RawTask) => {
        if (!isDone(t.status)) return <Text type="secondary">—</Text>;
        const sum = (t.scores || []).reduce((s, r) => s + (r.score || 0), 0);
        const max = taskFullScore(t);
        return (
          <Tag color="purple" style={{ margin: 0, fontWeight: 600 }}>
            {sum} / {max}
          </Tag>
        );
      },
    },
    {
      title: (
        <Tooltip title="按规则：同类多人求平均得代表分 → 缺类归一 → Σ(代表分 × 归一权重)">
          <span>加权总分</span>
        </Tooltip>
      ),
      key: 'weighted',
      width: 110,
      align: 'center' as const,
      onCell: (_t: RawTask, idx?: number) => mergedEvaluateeCell(idx ?? 0),
      render: (_v: unknown, t: RawTask) => {
        const agg = aggByEvaluatee.get(t.evaluatee.id);
        if (!agg) return <Text type="secondary">—</Text>;
        return (
          <Tag color="purple" style={{ margin: 0, fontWeight: 700 }}>
            {agg.weightedTotal.toFixed(2)} / {agg.fullScore}
          </Tag>
        );
      },
    },
    {
      title: (
        <Tooltip title="加权总分 ÷ 满分 × 100，保留 2 位">
          <span>综合素质标准分</span>
        </Tooltip>
      ),
      key: 'standard',
      width: 120,
      align: 'center' as const,
      onCell: (_t: RawTask, idx?: number) => mergedEvaluateeCell(idx ?? 0),
      render: (_v: unknown, t: RawTask) => {
        const agg = aggByEvaluatee.get(t.evaluatee.id);
        if (!agg) return <Text type="secondary">—</Text>;
        // 标准分按区间染色：≥85 绿、≥70 蓝、≥55 金、否则红
        const v = agg.standardScore;
        const color = v >= 85 ? 'green' : v >= 70 ? 'blue' : v >= 55 ? 'gold' : 'red';
        return (
          <Tag color={color} style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>
            {v.toFixed(2)}
          </Tag>
        );
      },
    },
    {
      title: (
        <Tooltip title="基于全批次所有有标准分的被评人排序：前 30% 高、中间 50% 中、后 20% 低（人少时按 <5/5–14/≥15 三档兜底）">
          <span>素质项等级</span>
        </Tooltip>
      ),
      key: 'grade',
      width: 110,
      align: 'center' as const,
      onCell: (_t: RawTask, idx?: number) => mergedEvaluateeCell(idx ?? 0),
      render: (_v: unknown, t: RawTask) => {
        const g = gradeByEvaluatee.get(t.evaluatee.id);
        if (!g) return <Text type="secondary">—</Text>;
        const color = g.grade === '高' ? 'green' : g.grade === '中' ? 'blue' : 'red';
        return (
          <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
            <Tag color={color} style={{ margin: 0, fontWeight: 700 }}>
              {g.grade}
            </Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>
              全员 {g.rank}/{g.total}
            </Text>
          </Space>
        );
      },
    },
  ];

  const exportCsv = () => {
    if (flatRows.length === 0) return;
    // 列顺序与表格保持一致：单评价人维度分 + 单评价人总分 / 被评人加权总分 / 标准分 / 素质项等级
    const headers = [
      '被评价人',
      '部门',
      ...(isAllPools ? ['人才池'] : []),
      '校准(行)',
      '评价人',
      '关系',
      '关系权重(%)',
      '状态',
      ...dims.map((d) => d.name || d.code),
      '总分',
      '满分',
      '加权总分',
      '综合素质标准分',
      '素质项等级',
      '全员排名',
    ];
    const lines: string[] = [headers.map(csvEscape).join(',')];
    for (const t of flatRows) {
      const submitted = isDone(t.status);
      const scoreMap = new Map((t.scores || []).map((s) => [s.dimCode, s.score]));
      const sum = submitted
        ? (t.scores || []).reduce((s, r) => s + (r.score || 0), 0)
        : '';
      const agg = aggByEvaluatee.get(t.evaluatee.id);
      const grade = gradeByEvaluatee.get(t.evaluatee.id);
      const rowCalDone = calibrationByEvaluatee.get(t.evaluatee.id) === true;
      const w = (t.template.relationWeights as Record<string, number> | null | undefined)?.[
        t.roleInEval
      ];
      const wPct = typeof w === 'number' ? w : '';
      const row = [
        t.evaluatee.name,
        shortDept(t.evaluatee.deptPath),
        ...(isAllPools ? [getTalentPoolLabel(t.template.pool)] : []),
        rowCalDone ? '已校准' : '待校准',
        t.evaluator.name,
        getEvalRoleLabel(t.template.pool, t.roleInEval),
        wPct,
        taskStatusLabel(t.status),
        ...dims.map((d) => (submitted ? (scoreMap.get(d.code) ?? '') : '')),
        sum,
        submitted ? taskFullScore(t) : '',
        agg ? agg.weightedTotal.toFixed(2) : '',
        agg ? agg.standardScore.toFixed(2) : '',
        grade ? grade.grade : '',
        grade ? `${grade.rank}/${grade.total}` : '',
      ];
      lines.push(row.map((v) => csvEscape(String(v))).join(','));
    }
    const bom = '\ufeff';
    const blob = new Blob([bom + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const batchName = batchLabel(batches.find((b) => b.id === batchId)) || 'batch';
    const poolName = isAllPools
      ? '全部人才池'
      : getTalentPoolLabel(effectivePool) || effectivePool || 'pool';
    a.href = url;
    a.download = `素质项评价结果-${batchName}-${poolName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">素质项 · 评价结果一览</div>

      <Card style={{ ...adminFilterCardStyle, marginBottom: 12 }} styles={{ body: { paddingBottom: 12 } }}>
        <Space wrap size={12}>
          <span>批次：</span>
          <Select
            placeholder="选择批次"
            style={{ width: 220 }}
            options={batches.map((b) => ({ label: batchLabel(b), value: b.id }))}
            onChange={setBatchId}
            value={batchId}
            loading={loadingBatches}
          />
          <span style={{ marginLeft: 8 }}>人才池：</span>
          <Select
            placeholder="选择人才池"
            style={{ width: 160 }}
            options={poolOptions}
            value={effectivePool}
            onChange={setPool}
            disabled={!batchId}
          />
          <Input
            placeholder={KW_FIELD_OPTIONS.find((o) => o.value === kwField)?.placeholder}
            allowClear
            style={{ width: 320 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            addonBefore={
              <Select
                value={kwField}
                onChange={setKwField}
                style={{ width: 110 }}
                options={KW_FIELD_OPTIONS.map(({ value, label }) => ({ value, label }))}
              />
            }
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportCsv}
            disabled={flatRows.length === 0}
          >
            导出 CSV
          </Button>
        </Space>

        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={6}>
            <Statistic
              title="被评价人"
              value={stats.evaluatees}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              suffix="人"
            />
          </Col>
          <Col span={6}>
            <Statistic title="评价记录" value={stats.total} suffix="条" />
          </Col>
          <Col span={6}>
            <Statistic
              title="已提交"
              value={stats.submitted}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="待提交"
              value={stats.pending}
              valueStyle={{ color: stats.pending > 0 ? '#fa8c16' : '#999' }}
              prefix={<ClockCircleTwoTone twoToneColor={stats.pending > 0 ? '#fa8c16' : '#999'} />}
            />
          </Col>
        </Row>
        {isAllPools ? (
          <div style={{ marginTop: 8 }}>
            <Tag color="purple">全部人才池</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              显示所有池的被评价人；各池模板维度不同，总分/满分按各行自己的模板计算，维度明细请选择具体人才池查看
            </Text>
          </div>
        ) : effectivePool ? (
          <div style={{ marginTop: 8 }}>
            <Tag color={POOL_COLORS[effectivePool] || 'default'}>
              {getTalentPoolLabel(effectivePool)}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              当前池模板维度共 {dims.length} 项 · 满分 {dims.length * 7}
            </Text>
          </div>
        ) : null}
      </Card>

      <Card style={adminContentCardStyle} styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
        {!batchId ? (
          <Empty description="请先选择批次" />
        ) : isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : flatRows.length === 0 ? (
          <Empty description="该筛选下暂无评价记录" />
        ) : (
          <Table<RawTask>
            className="tg-eval-results-matrix-table"
            rowKey="id"
            size="small"
            sticky
            scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
            dataSource={flatRows}
            columns={columns}
            rowClassName={evaluateeGroupRowClassName}
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
          />
        )}
      </Card>
    </div>
  );
}

function csvEscape(v: string): string {
  if (v == null) return '';
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

/**
 * 单维度分数格子（V1.3 校准）。
 *   - 未校准：原色 Tag（按 0-7 区间染色）
 *   - 已校准：大红底铺满整个格子，且在分数旁显示原始评价人评分（带删除线小灰字），hover 显示校准人/校准时间
 *   - admin 点击 → 弹 Popover 内填新分（0-7 整数）→ 提交后由父组件重新拉数据
 */
function ScoreCell({
  score,
  dimName,
  onSubmit,
  submitting,
}: {
  score: ScoreRow;
  dimName: string;
  onSubmit: (newScore: number) => Promise<unknown>;
  submitting: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<number | null>(score.score);

  const v = score.score;
  const color = v >= 6 ? 'green' : v >= 4 ? 'gold' : v >= 2 ? 'orange' : 'red';
  const adjusted = score.adjustedFromScore != null;

  const tagBaseStyle: React.CSSProperties = {
    margin: 0,
    fontWeight: 600,
    minWidth: 36,
    textAlign: 'center',
    cursor: 'pointer',
  };

  // 校准过的格子：整格大红底 + 白字校准分 + 删除线小灰字原始评分
  const cellWrapperStyle: React.CSSProperties = adjusted
    ? {
        background: '#ff4d4f',
        borderRadius: 4,
        padding: '4px 8px',
        boxShadow: 'inset 0 0 0 1px #cf1322',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
      }
    : { cursor: 'pointer' };

  const popoverContent = (
    <div style={{ minWidth: 220 }}>
      <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
        校准维度：<b>{dimName}</b>
      </div>
      {adjusted && (
        <div
          style={{
            marginBottom: 8,
            fontSize: 12,
            color: '#cf1322',
            background: '#fff1f0',
            border: '1px solid #ffccc7',
            borderRadius: 4,
            padding: '6px 8px',
          }}
        >
          <div>
            评价人原始评分：<b style={{ fontSize: 14 }}>{score.adjustedFromScore}</b>
            <span style={{ margin: '0 6px', color: '#999' }}>→</span>
            校准后：<b style={{ fontSize: 14 }}>{score.score}</b>
          </div>
          {score.adjustedBy?.name && (
            <div style={{ color: '#999', marginTop: 2 }}>
              上次校准：{score.adjustedBy.name}
              {score.adjustedAt &&
                ` · ${new Date(score.adjustedAt).toLocaleString('zh-CN', { hour12: false })}`}
            </div>
          )}
        </div>
      )}
      <Space>
        <InputNumber
          min={0}
          max={7}
          step={1}
          precision={0}
          value={draft}
          onChange={(n) => setDraft(typeof n === 'number' ? n : null)}
          style={{ width: 90 }}
          autoFocus
        />
        <Button
          type="primary"
          size="small"
          loading={submitting}
          disabled={draft == null || draft < 0 || draft > 7}
          onClick={async () => {
            if (draft == null) return;
            try {
              await onSubmit(draft);
              setOpen(false);
            } catch {
              // 错误已由 mutation onError 全局提示
            }
          }}
        >
          保存
        </Button>
        <Button size="small" onClick={() => setOpen(false)}>
          取消
        </Button>
      </Space>
      <div style={{ marginTop: 6, fontSize: 11, color: '#999' }}>0–7 整数；保存后会自动重算加权总分与等级。</div>
    </div>
  );

  return (
    <Popover
      open={open}
      trigger="click"
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setDraft(score.score);
      }}
      content={popoverContent}
      title={
        <Space>
          <EditOutlined />
          <span>校准 · {dimName}</span>
        </Space>
      }
      placement="top"
    >
      <span
        style={cellWrapperStyle}
        title={
          adjusted
            ? `已校准 · 原始评价 ${score.adjustedFromScore} 分${
                score.adjustedBy?.name ? ` · 校准人 ${score.adjustedBy.name}` : ''
              }${
                score.adjustedAt
                  ? ` · ${new Date(score.adjustedAt).toLocaleString('zh-CN', { hour12: false })}`
                  : ''
              }`
            : '点击校准'
        }
      >
        {adjusted ? (
          <>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, minWidth: 20, textAlign: 'center' }}>
              {v}
            </span>
            <span
              style={{
                color: '#ffd9d9',
                fontSize: 11,
                textDecoration: 'line-through',
                fontWeight: 500,
              }}
            >
              原 {score.adjustedFromScore}
            </span>
          </>
        ) : (
          <Tag color={color} style={tagBaseStyle}>
            {v}
          </Tag>
        )}
      </span>
    </Popover>
  );
}
